import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Search, Activity, ArrowRight, AlertCircle, ArrowLeft, Sun, Moon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Toaster } from '../components/ui/sonner';
import { HealthCrossLogo } from '../components/HealthCrossLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SymptomSelect = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, language } = useLanguage();
  const [symptoms, setSymptoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSymptom, setSelectedSymptom] = useState(null);

  useEffect(() => {
    fetchSymptoms();
  }, []);

  const fetchSymptoms = async () => {
    try {
      const response = await axios.get(`${API}/symptoms`);
      setSymptoms(response.data);
    } catch (error) {
      console.error('Failed to fetch symptoms:', error);
      toast.error('Failed to load symptoms');
    } finally {
      setLoading(false);
    }
  };

  const filteredSymptoms = symptoms.filter(s => {
    const name = language === 'pt' && s.name_pt ? s.name_pt : s.name;
    const category = language === 'pt' && s.category_pt ? s.category_pt : s.category;
    return name.toLowerCase().includes(search.toLowerCase()) ||
      category?.toLowerCase().includes(search.toLowerCase());
  });

  const groupedSymptoms = filteredSymptoms.reduce((acc, symptom) => {
    const category = language === 'pt' && symptom.category_pt ? symptom.category_pt : (symptom.category || 'Other');
    if (!acc[category]) acc[category] = [];
    acc[category].push(symptom);
    return acc;
  }, {});

  // Helper to get translated name
  const getSymptomName = (symptom) => {
    return language === 'pt' && symptom.name_pt ? symptom.name_pt : symptom.name;
  };

  const getSymptomDescription = (symptom) => {
    return language === 'pt' && symptom.description_pt ? symptom.description_pt : symptom.description;
  };

  const handleContinue = () => {
    if (selectedSymptom) {
      navigate(`/diagnose/${selectedSymptom.id}`);
    }
  };

  // Guest header
  const GuestHeader = () => (
    <header className="fixed top-0 left-0 right-0 z-40 glass border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <HealthCrossLogo className="w-10 h-10" iconClassName="w-5 h-5" />
          <span className="font-bold text-lg hidden sm:block">{t('appName')}</span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full"
          >
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => navigate('/auth')}
            data-testid="sign-in-btn"
          >
            {t('signIn')}
          </Button>
        </div>
      </div>
    </header>
  );

  return (
    <>
      {!user && <GuestHeader />}
      <div className={`max-w-2xl mx-auto px-6 py-8 ${!user ? 'pt-24' : ''}`} data-testid="symptom-select">
        <Toaster position="top-center" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <span className="label-medical">{t('step1')}</span>
          <h1 className="text-3xl font-bold mt-2 mb-2">{t('selectYourSymptom')}</h1>
          <p className="text-muted-foreground mb-8">
            {t('chooseMainSymptom')}
          </p>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('searchSymptoms')}
              className="pl-12 rounded-full"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="symptom-search"
            />
          </div>

          {/* Symptoms list */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map(i => (
                <Card key={i} className="p-4 rounded-2xl animate-pulse">
                  <div className="h-5 bg-muted rounded w-1/3" />
                </Card>
              ))}
            </div>
          ) : Object.keys(groupedSymptoms).length > 0 ? (
            <div className="space-y-6 pb-24">
              {Object.entries(groupedSymptoms).map(([category, categorySymptoms]) => (
                <div key={category}>
                  <span className="label-medical mb-3 block">{category}</span>
                  <div className="grid gap-3">
                    {categorySymptoms.map((symptom, index) => (
                      <motion.div
                        key={symptom.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.05 }}
                      >
                        <Card
                          className={`p-4 rounded-2xl cursor-pointer transition-all card-hover ${
                            selectedSymptom?.id === symptom.id
                              ? 'border-2 border-primary bg-primary/5'
                              : 'border-2 border-transparent hover:border-border'
                          }`}
                          onClick={() => setSelectedSymptom(symptom)}
                          data-testid={`symptom-${symptom.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                selectedSymptom?.id === symptom.id
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-muted'
                              }`}>
                                <Activity className="w-5 h-5" />
                              </div>
                              <div>
                                <h3 className="font-semibold">{getSymptomName(symptom)}</h3>
                                {getSymptomDescription(symptom) && (
                                  <p className="text-sm text-muted-foreground">{getSymptomDescription(symptom)}</p>
                                )}
                              </div>
                            </div>
                            {selectedSymptom?.id === symptom.id && (
                              <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                <svg className="w-4 h-4 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Card className="p-8 rounded-2xl text-center">
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">{t('noSymptomsFound')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('tryDifferentSearch')}
              </p>
            </Card>
          )}

          {/* Continue button */}
          {selectedSymptom && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="fixed bottom-6 left-0 right-0 px-6"
            >
              <div className="max-w-2xl mx-auto">
                <Button
                  className="w-full rounded-full btn-scale shadow-lg"
                  size="lg"
                  onClick={handleContinue}
                  data-testid="continue-btn"
                >
                  {t('continueWith')} {getSymptomName(selectedSymptom)}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </>
  );
};
